import { SxProps, TextField } from "@mui/material";
import { useWatch } from "react-hook-form";

import type {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
  FormFieldValidationDefinition,
} from "../form-field";

export const TEXT_FORM_FIELD_TYPE = "text" as const;

export type TextFormFieldDefinition = BaseFormFieldDefinition<
  typeof TEXT_FORM_FIELD_TYPE
> & {
  type: typeof TEXT_FORM_FIELD_TYPE;
  maxLength?: FormFieldValidationDefinition<number>;
  minLength?: FormFieldValidationDefinition<number>;
  multiline?: boolean;
  placeholder?: string;
  pattern?: FormFieldValidationDefinition<RegExp>;
  sx?: SxProps;
  showCharacterCount?: boolean;
};

function TextFieldCharacterCount({
  name,
  maxLength,
}: {
  name: string;
  maxLength: FormFieldValidationDefinition<number>;
}) {
  const value = useWatch({ name });
  const count = typeof value === "string" ? value.length : 0;

  return (
    <span aria-live="polite" style={{ display: "block", textAlign: "right" }}>
      {count} / {maxLength.value} characters
    </span>
  );
}

export function TextFormField({
  disabled,
  errorMessage,
  maxLength,
  minLength,
  multiline,
  name,
  pattern,
  placeholder,
  register,
  required,
  showCharacterCount,
  sx,
}: TextFormFieldDefinition & AdditionalFormFieldProps) {
  return (
    <TextField
      {...register(name, { disabled, maxLength, minLength, pattern, required })}
      error={!!errorMessage}
      fullWidth
      multiline={multiline}
      placeholder={placeholder}
      sx={sx}
      slotProps={{
        htmlInput: { maxLength: maxLength?.value },
        input: {
          style: {
            borderRadius: "6px",
            height: multiline ? "auto" : "45px",
          },
        },
      }}
      helperText={
        (errorMessage || (showCharacterCount && maxLength)) && (
          <>
            {errorMessage}
            {showCharacterCount && maxLength && (
              <TextFieldCharacterCount name={name} maxLength={maxLength} />
            )}
          </>
        )
      }
    />
  );
}
